import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Button, Input, Select, Table, Modal, message, Upload} from 'antd';
import {DownOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined} from '@ant-design/icons';
import type {UploadProps} from 'antd';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import EditableCard from 'components/EditableCard';
import {AreaType} from 'graphql/queries/getAreasByWarehouseId';
import {useDspsByWarehouseId} from 'graphql/queries/getDspsByWarehouseId';
import {useMutation, useLazyQuery, useApolloClient} from '@apollo/client';
import {DELETE_AREA_MUTATION, deleteAreaResponse, deleteAreaVariables} from '../graphql/mutations/deleteArea';
import {AreaTypeInput, VALIDATE_AND_PREVIEW_ROUTE_IMPORT, ValidateAndPreviewRouteImportResponse, ValidateAndPreviewRouteImportVariables, RouteImportRowInput} from '../graphql/mutations/areaMutations';
import { useTranslation } from 'react-i18next';
import {fetchZipcodeTakingStatus} from '../utils/fetchZipcodeTakingStatus';
import {PREVIEW_ROUTE_ZIPCODE_CHANGE_CROSS_WAREHOUSE, CrossWarehouseImpactItem} from '../graphql/queries/previewRouteZipcodeChangeCrossWarehouse';
import {downloadAreaRoutesExcel} from '../graphql/queries/downloadAreaRoutesExcel';
import {showRouteChangeErrorModal} from '../utils/routeChangeErrorModal';
import TablePaginationBar from '../../../../components/TablePaginationBar';


const { Option } = Select;
const { TextArea } = Input;

export interface AreaZipcodesFormData {
  areas: AreaType[];
}

interface AreaZipcodesFormProps {
  data: AreaZipcodesFormData;
  deliveryZoneZipcodes?: string[]; // 新增：用于验证的delivery zone zipcodes
  warehouseSortingCode?: string; // warehouse的sortingCode
  warehouseId?: number; // 新增：仓库ID用于查询DSP
  warehouseName?: string; // 仓库名称，用于导出文件名
  onChange?: (data: AreaZipcodesFormData) => void;
  isEditing?: boolean;
  isGrayWarehouse?: boolean; // 灰度仓：走编辑态+确认生效新流程
  updateInProgress?: boolean; // 灰度仓：正在「更新运单数据中」，禁编辑
  onEdit?: () => void;
  onSave?: (data: AreaZipcodesFormData) => void; // 非灰度仓：即改即生效
  onConfirmGray?: (payload: { areas: AreaTypeInput[]; deletedAreaIds: number[] }) => void | Promise<void>; // 灰度仓：确认生效
  onCancel?: () => void;
  onDataRefresh?: () => void; // 新增：数据刷新回调
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}

const AreaZipcodesForm: React.FC<AreaZipcodesFormProps> = ({
  data,
  deliveryZoneZipcodes = [],
  warehouseSortingCode,
  warehouseId,
  warehouseName,
  onChange,
  isEditing = false,
  isGrayWarehouse = false,
  updateInProgress = false,
  onEdit,
  onSave,
  onConfirmGray,
  onCancel,
  onDataRefresh,
  title,
  loading = false,
  bordered = false,
}) => {
  const { t, i18n } = useTranslation();
  type InternalAreaType = AreaType & {
    zipcodeListText: string;
  }

  const [internalAreas, setInternalAreas] = useState<InternalAreaType[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // 灰度仓「拟删除」的已存在路线 id 集合（本地标记，确认生效时才真正删除）
  const [markedForDeletion, setMarkedForDeletion] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // 确保页面大小变化时重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const { dsps: allDsps } = useDspsByWarehouseId(warehouseId);
  const [deleteAreaMutation] = useMutation(DELETE_AREA_MUTATION);
  // 预览是「暂存、未落库」的结果，返回的 AreaType（带 id）默认会写入规范化缓存、覆盖 GET_AREAS 里的真值，
  // 导致取消编辑后仍显示未确认的导入数据、且缓存被污染。用 no-cache 禁止写缓存。
  const [validateAndPreviewRouteImport] = useMutation<ValidateAndPreviewRouteImportResponse, ValidateAndPreviewRouteImportVariables>(
    VALIDATE_AND_PREVIEW_ROUTE_IMPORT,
    { fetchPolicy: 'no-cache' },
  );
  const apolloClient = useApolloClient();
  const [getCrossWarehouseImpact] = useLazyQuery(PREVIEW_ROUTE_ZIPCODE_CHANGE_CROSS_WAREHOUSE);
  
  // 存储邮编的 isTakingDeliveryOrder 状态
  const [zipcodeStatusMap, setZipcodeStatusMap] = useState<Map<string, boolean>>(new Map());

  // 辅助函数：从完整sortingCode提取后缀
  const getSortingCodeSuffix = (fullCode: string) => {
    if (!fullCode || !warehouseSortingCode) return '';
    const prefix = warehouseSortingCode + '-';
    if (fullCode.startsWith(prefix)) {
      return fullCode.substring(prefix.length);
    }

    if (fullCode === warehouseSortingCode) {
      return '';
    }
    // 其他情况返回空字符串
    return '';
  };

  // 四段展示：从完整 sortingCode 解析出第三段（字母）和第四段
  const parseSortingCodeToSegments = (fullCode: string): { segment3: string; segment4: string } => {
    const suffix = getSortingCodeSuffix(fullCode);
    if (!suffix) return { segment3: '', segment4: '' };
    const letterMatch = suffix.match(/^([A-Za-z]+)/);
    const digitMatch = suffix.match(/\d+/);
    const segment3 = letterMatch ? letterMatch[1] : '';
    const segment4 = digitMatch ? digitMatch[0] : '';
    return { segment3, segment4 };
  };

  // 四段组装：第三段（字母）+ 第四段（数字）→ 完整 sortingCode，数据库仍存一段
  const buildFullSortingCodeFromSegments = (segment3: string, segment4: string) => {
    if (!warehouseSortingCode) return segment3 + segment4;
    const suffix = (segment3 || '') + (segment4 || '');
    if (!suffix) return warehouseSortingCode;

    return `${warehouseSortingCode}-${suffix}`;
  };

  // 统一的验证函数，通过参数控制是否跳过空行
  const validateAreas = useCallback((areas: InternalAreaType[], skipEmptyRows = true) => {
    const errors: string[] = [];

    // 收集所有zipcode来检查重复
    const zipcodeMap = new Map<string, number[]>(); // zipcode -> area indices
    // 收集所有sorting code来检查重复
    const sortingCodeMap = new Map<string, number[]>(); // sortingCode -> area indices
    // 收集所有路线名称来检查重复（与后端 confirmRouteZipcodeChange 大小写敏感精确匹配保持一致）
    const nameMap = new Map<string, number[]>(); // name -> area indices
    
    areas.forEach((area, index) => {
      const areaId = area.id || `${index + 1}`;
      
      // 判断是否为新增的空行
      const isNewEmptyRow = !area.id &&
        (!area.name || area.name.trim() === '' || area.name.startsWith('Area ')) &&
        area.zipcodeList.length === 0 &&
        (!area.dspId || area.dspId === '') &&
        (!area.sortingCode || area.sortingCode.trim() === '');
      
      // 如果需要跳过空行且当前是空行，则跳过验证
      if (skipEmptyRows && isNewEmptyRow) {
        return;
      }
      
      // 1. 验证 zipcodes 是否都在 delivery zone 中（灰度仓不再校验派送区域范围）
      if (!isGrayWarehouse) {
        const invalidZipcodes = area.zipcodeList.filter(zipcode => !deliveryZoneZipcodes.includes(zipcode));
        if (invalidZipcodes.length > 0) {
          errors.push(`Area ${areaId}: Zipcodes not in delivery zone: ${invalidZipcodes.join(', ')}`);
        }
      }
      
      // 2. 验证 Zipcode 不能为空
      if (!area.zipcodeList || area.zipcodeList.length === 0) {
        errors.push(`Area ${areaId}: Zipcode is required`);
      }

      // 3. 验证 DSP 不能为空
      if (!area.dspId || area.dspId === '') {
        errors.push(`Area ${areaId}: Assign DSP is required`);
      }
      
        // 4. 验证 Sorting Code：第三段（字母）和第四段（数字）都必须填写；第三段仅字母，第四段仅数字
        if (!area.sortingCode || area.sortingCode.trim() === '') {
          errors.push(`Area ${areaId}: Sorting Code is required`);
        } else {
          // 检查总长度不能超过32位
          if (area.sortingCode.length > 32) {
            errors.push(`Area ${areaId}: Sorting Code cannot exceed 32 characters`);
          } else {
            const { segment3, segment4 } = parseSortingCodeToSegments(area.sortingCode);
            const hasSegment3 = segment3.length > 0;
            const hasSegment4 = segment4.length > 0;
            if (!hasSegment3 || !hasSegment4) {
              errors.push(`Area ${areaId}: Sorting Code requires both letter and digit segments`);
            } else {
              if (hasSegment3 && !/^[A-Za-z]+$/.test(segment3)) {
                errors.push(`Area ${areaId}: Sorting Code third segment must be letters only (A-Z)`);
              }
              if (hasSegment4 && !/^\d+$/.test(segment4)) {
                errors.push(`Area ${areaId}: Sorting Code fourth segment must be digits only`);
              }
            }
          }
        }
      
      // 5. 验证 Name 不能为空
      if (!area.name || area.name.trim() === '') {
        errors.push(`Area ${areaId}: Name is required`);
      }

      // 6. 收集zipcode用于重复检查
      area.zipcodeList.forEach(zipcode => {
        if (zipcode) {
          if (!zipcodeMap.has(zipcode)) {
            zipcodeMap.set(zipcode, []);
          }
          zipcodeMap.get(zipcode)!.push(index);
        }
      });
      
      // 7. 收集sorting code用于重复检查
      if (area.sortingCode && area.sortingCode.trim() !== '') {
        const sortingCode = area.sortingCode.trim();
        if (!sortingCodeMap.has(sortingCode)) {
          sortingCodeMap.set(sortingCode, []);
        }
        sortingCodeMap.get(sortingCode)!.push(index);
      }

      // 8. 收集路线名称用于重复检查（非空即收集，与后端一致不做 trim/大小写归一）
      if (area.name && area.name.trim() !== '') {
        if (!nameMap.has(area.name)) {
          nameMap.set(area.name, []);
        }
        nameMap.get(area.name)!.push(index);
      }
    });

    // 9. 检查zipcode重复
    zipcodeMap.forEach((areaIndices, zipcode) => {
      if (areaIndices.length > 1) {
        const areaIds = areaIndices.map(i => areas[i].id || `${i + 1}`);
        errors.push(`Zipcode ${zipcode} is used in multiple areas: ${areaIds.join(', ')}`);
      }
    });

    // 10. 检查sorting code重复
    sortingCodeMap.forEach((areaIndices, sortingCode) => {
      if (areaIndices.length > 1) {
        const areaIds = areaIndices.map(i => areas[i].id || `${i + 1}`);
        errors.push(`Sorting Code "${sortingCode}" is used in multiple areas: ${areaIds.join(', ')}`);
      }
    });

    // 11. 检查路线名称重复（前置校验，编辑态实时提示，避免二次确认时才由后端报错）
    nameMap.forEach((areaIndices, name) => {
      if (areaIndices.length > 1) {
        const areaIds = areaIndices.map(i => areas[i].id || `${i + 1}`);
        errors.push(`Area name "${name}" is used in multiple areas: ${areaIds.join(', ')}`);
      }
    });

    return errors;
  }, [deliveryZoneZipcodes, isGrayWarehouse]);

  // 实时验证（跳过空行；灰度仓跳过拟删除行）
  const validateAllAreas = useCallback((areas: InternalAreaType[]) => {
    const activeAreas = areas.filter(a => !(a.id && markedForDeletion.has(a.id)));
    const errors = validateAreas(activeAreas, true);
    setValidationErrors(errors);
  }, [validateAreas, markedForDeletion]);
  
  useEffect(() => {
    const newAreas = data.areas.map(area => ({
      ...area,
      zipcodeListText: area.zipcodeList.join(', ')
    }));
    setInternalAreas(newAreas);
    
    setValidationErrors([]);
  }, [data.areas, isEditing]);

  // 批量查询所有邮编的 isTakingDeliveryOrder 状态
  useEffect(() => {
    if (isEditing || data.areas.length === 0) {
      return;
    }
    // 收集所有邮编
    const allZipcodes = data.areas.flatMap(area => area.zipcodeList.filter(Boolean));
    if (allZipcodes.length === 0) {
      return;
    }
    let cancelled = false;
    fetchZipcodeTakingStatus(apolloClient, allZipcodes)
      .then((statusMap) => {
        if (!cancelled) {
          setZipcodeStatusMap(statusMap);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch zipcode status:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [data.areas, isEditing, apolloClient]);

  // 监听编辑状态变化，取消编辑时重置为原始数据
  useEffect(() => {
    if (!isEditing) {
      const resetAreas = data.areas.map(area => ({
        ...area,
        zipcodeListText: area.zipcodeList.join(', ')
      }));
      setInternalAreas(resetAreas);
      setValidationErrors([]);
      setCurrentPage(1); // 重置分页
      setMarkedForDeletion(new Set()); // 取消编辑清空拟删除标记
    }
  }, [isEditing, data.areas]);

  const saveInternalAreas = (index: number, partialArea: Partial<InternalAreaType>) => {
    const newAreas = [...internalAreas];
    newAreas[index] = {
      ...newAreas[index],
      ...partialArea,
    };
    setInternalAreas(newAreas);
  };

  // 1. 渲染前为每行加 rowIndex
  const areasWithIndex = internalAreas.map((area, idx) => ({ ...area, rowIndex: idx }));

  // 2. 所有 handleChangeXXX 用 rowIndex 定位
  const findAreaIndex = (rowIndex: number) => internalAreas[rowIndex] ? rowIndex : -1;

  const handleChangeDSP = (rowIndex: number, dspId: string) => {
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const newAreas = [...internalAreas];
      newAreas[idx] = {
        ...newAreas[idx],
        dspId,
      };
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
    }
  };

  const handleChangeZipcodes = (rowIndex: number, zipcodesText: string) => {
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const zipcodes = zipcodesText.split(',').map(code => code.trim()).filter(code => code !== '');
      const newAreas = [...internalAreas];
      newAreas[idx] = {
        ...newAreas[idx],
        zipcodeList: zipcodes,
        zipcodeListText: zipcodesText,
      };
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
    }
  };

  const handleChangeSortingCodeSegment3 = (rowIndex: number, value: string) => {
    const letterOnly = value.replace(/[^A-Za-z]/g, '');
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const { segment4 } = parseSortingCodeToSegments(internalAreas[idx].sortingCode || '');
      const fullSortingCode = buildFullSortingCodeFromSegments(letterOnly, segment4);
      const newAreas = [...internalAreas];
      newAreas[idx] = { ...newAreas[idx], sortingCode: fullSortingCode };
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
    }
  };

  const handleChangeSortingCodeSegment4 = (rowIndex: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const { segment3 } = parseSortingCodeToSegments(internalAreas[idx].sortingCode || '');
      const fullSortingCode = buildFullSortingCodeFromSegments(segment3, digitsOnly);
      const newAreas = [...internalAreas];
      newAreas[idx] = { ...newAreas[idx], sortingCode: fullSortingCode };
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
    }
  };

  const handleChangeName = (rowIndex: number, name: string) => {
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const newAreas = [...internalAreas];
      newAreas[idx] = {
        ...newAreas[idx],
        name,
      };
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
    }
  };

  // 3. 新增行时自动分配 rowIndex（push 到最后即可）
  const handleAddSection = () => {
    const newAreaIndex = internalAreas.length + 1;
    const newArea = {
      id: '',
      name: `Area ${newAreaIndex}`,
      warehouseId: '',
      zipcodeList: [],
      dspId: '',
      sortingCode: '',
      zipcodeListText: ''
    };
    const newAreas = [...internalAreas, newArea];
    setInternalAreas(newAreas);
    // 新增后自动跳转到最后一页
    const total = newAreas.length;
    const lastPage = Math.ceil(total / pageSize);
    setCurrentPage(lastPage);
  };

  // 删除区域
  const handleDeleteArea = (rowIndex: number) => {
    const idx = findAreaIndex(rowIndex);
    if (idx !== -1) {
      const areaToDelete = internalAreas[idx];

      // 如果是新添加的区域（没有ID），直接删除
      if (!areaToDelete.id) {
        const newAreas = internalAreas.filter((_, index) => index !== idx);
        setInternalAreas(newAreas);
        validateAllAreas(newAreas);
        return;
      }

      // 如果是已存在的区域，弹框确认后调用后端删除接口
      Modal.confirm({
        title: 'Delete Area',
        content: `Are you sure you want to delete area "${areaToDelete.name}" (Sorting Code: ${areaToDelete.sortingCode})? This action cannot be undone.`,
        okText: 'Delete',
        cancelText: 'Cancel',
        okType: 'danger',
        onOk: async () => {
          try {
            // 调用后端删除接口
            await deleteAreaFromBackend(areaToDelete.id);

            // 删除成功后从本地列表中移除
            const newAreas = internalAreas.filter((_, index) => index !== idx);
            setInternalAreas(newAreas);
            validateAllAreas(newAreas);
            // 刷新数据
            onDataRefresh?.();

            // 显示成功消息
            message.success(`Area "${areaToDelete.name}" has been deleted successfully.`);
          } catch (error: any) {
            // 删除失败，显示错误消息
            message.error(`Failed to delete area: ${error.message}`);
          }
        }
      });
    }
  };

  // 灰度仓：拟删除/取消删除（本地标记，不即时调后端）
  const handleToggleMarkDelete = (rowIndex: number) => {
    const idx = findAreaIndex(rowIndex);
    if (idx === -1) return;
    const area = internalAreas[idx];
    // 新增行（无 id）直接从列表移除
    if (!area.id) {
      const newAreas = internalAreas.filter((_, index) => index !== idx);
      setInternalAreas(newAreas);
      validateAllAreas(newAreas);
      return;
    }
    const next = new Set(markedForDeletion);
    if (next.has(area.id)) {
      next.delete(area.id);
    } else {
      next.add(area.id);
    }
    setMarkedForDeletion(next);
    // 标记变化后重新校验（拟删除行不参与校验）
    const activeAreas = internalAreas.filter(a => !(a.id && next.has(a.id)));
    setValidationErrors(validateAreas(activeAreas, true));
  };

  // 调用后端删除区域接口
  const deleteAreaFromBackend = async (areaId: string) => {
    try {
      const {data} = await deleteAreaMutation({
        variables: {areaId: parseInt(areaId)}
      });

      if (data?.deleteArea) {
        return true;
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error: any) {
      console.error('Delete area error:', error);
      throw new Error(error.message || 'Failed to delete area');
    }
  };

  // DSP table column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'left' as const,
      render: (id: string) => id || '',
    },
    {
      title: t('central.sites.details.name'),
      dataIndex: 'name',
      key: 'name',
      width: 120,
      align: 'left' as const,
      render: (name: string, record: InternalAreaType & { rowIndex: number }) => (
        isEditing ? (
          <Input
            value={name}
            onChange={(e) => handleChangeName(record.rowIndex, e.target.value)}
            placeholder="Enter area name"
          />
        ) : (
          name
        )
      ),
    },
    {
      title: t('central.sites.details.zipcodes'),
      dataIndex: 'zipcodeListText',
      key: 'zipcodeListText',
      width: 320,
      align: 'left' as const,
      onCell: () => ({
        style: {
          minWidth: 320
        }
      }),
      render: (zipcodeListText: string, record: InternalAreaType & { rowIndex: number }) => {
        if (isEditing) {
          return (
            <TextArea
              value={zipcodeListText}
              rows={2}
              onChange={(e) => handleChangeZipcodes(record.rowIndex, e.target.value)}
              placeholder="Enter zipcodes, separated by commas"
              style={{ width: '100%', minWidth: 320 }}
            />
          );
        }
        
        // 非编辑模式下，将未接单的邮编显示为紫色
        const zipcodes = record.zipcodeList;
        if (zipcodes.length === 0) {
          return zipcodeListText;
        }
        
        return (
          <span>
            {zipcodes.map((zipcode, index) => {
              const isTakingOrder = zipcodeStatusMap.get(zipcode) ?? true; // 默认值为 true
              const color = isTakingOrder ? undefined : '#722ed1'; // 紫色
              return (
                <span key={zipcode}>
                  {index > 0 && ', '}
                  <span style={{ color }}>{zipcode}</span>
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t('central.sites.details.count'),
      key: 'count',
      width: 50,
      align: 'left' as const,
      render: (_: any, record: InternalAreaType) => record.zipcodeList.length,
    },
    {
      title: t('central.sites.details.takingOrders'),
      key: 'takingOrders',
      width: 80,
      align: 'left' as const,
      render: (_: any, record: InternalAreaType) => {
        // 计算该 area 中接受订单的 zipcode 数量
        const takingOrdersCount = record.zipcodeList.filter(zipcode => {
          const isTakingOrder = zipcodeStatusMap.get(zipcode) ?? true; // 默认值为 true
          return isTakingOrder;
        }).length;
        return takingOrdersCount;
      },
    },
    {
      title: t('central.sites.details.shortingCode'),
      dataIndex: 'sortingCode',
      key: 'sortingCode',
      width: 280,
      align: 'left' as const,
      render: (sortingCode: string, record: InternalAreaType & { rowIndex: number }) => {
        const { segment3, segment4 } = parseSortingCodeToSegments(sortingCode || '');
        const hasError = validationErrors.some(error =>
          error.includes(`Area ${record.id || `${record.rowIndex + 1}`}`) &&
          error.includes('Sorting Code')
        );
        const errorMessage = validationErrors.find(error =>
          error.includes(`Area ${record.id || `${record.rowIndex + 1}`}`) &&
          error.includes('Sorting Code')
        );
        // 灰度仓：分拣码对已存在路线也可编辑；非灰度仓保持仅新增行可编辑
        const isSegmentCodeDisabled = isGrayWarehouse ? false : !!record.id;

        return (
            isEditing ? (
                <div className="w-full">
                  <div className="flex flex-wrap items-center gap-0.5">
                    {warehouseSortingCode && (
                        <span className="text-gray-500 shrink-0">{warehouseSortingCode}</span>
                    )}
                    <span className="text-gray-500 shrink-0">-</span>
                    <Input
                        value={segment3}
                        onChange={(e) => handleChangeSortingCodeSegment3(record.rowIndex, e.target.value)}
                        disabled={isSegmentCodeDisabled}
                        style={{ width: 48 }}
                        status={hasError ? 'error' : undefined}
                        placeholder=""
                    />
                    <span className="text-gray-500 shrink-0">-</span>
                    <Input
                        value={segment4}
                        onChange={(e) => handleChangeSortingCodeSegment4(record.rowIndex, e.target.value)}
                        disabled={isSegmentCodeDisabled}
                        style={{ width: 56 }}
                        status={hasError ? 'error' : undefined}
                        placeholder=""
                    />
                  </div>
                  {hasError && errorMessage && (
                    <div className="text-red-500 text-xs mt-1">
                      {errorMessage.replace(`Area ${record.id || `${record.rowIndex + 1}`}: `, '')}
                    </div>
                  )}
                </div>
            ) : (
                sortingCode
            )
        );
      },
    },
    {
      title: t('central.sites.details.assignDsp'),
      dataIndex: 'dspId',
      key: 'dspId',
      align: 'left' as const,
      width: 220,
      render: (dspId: string, record: InternalAreaType & { rowIndex: number }) => (
        <Select
          value={dspId || undefined}
          placeholder={t('common.pleaseSelect')}
          className="w-full"
          suffixIcon={<DownOutlined />}
          disabled={!isEditing}
          onChange={(value) => handleChangeDSP(record.rowIndex, value)}
        >
          {allDsps.map(dsp => (
            <Option key={dsp.id} value={dsp.id}>{dsp.name}</Option>
          ))}
        </Select>
      ),
    },
    // 只在编辑状态下显示Actions列
    ...(isEditing ? [{
      title: t('central.sites.details.actions'),
      key: 'actions',
      width: 80,
      align: 'left' as const,
      render: (_: any, record: InternalAreaType & { rowIndex: number }) => {
        // 灰度仓：已存在路线走「拟删除」二次确认；新增行与非灰度仓走原删除
        if (isGrayWarehouse && record.id) {
          const isMarked = markedForDeletion.has(record.id);
          // 拟删除态：整行置灰（由 rowClassName 处理），删除按钮变为「拟删除」状态标识 + 「取消删除」
          if (isMarked) {
            return (
              <div className="flex justify-start items-center gap-1">
                <Button type="text" danger size="small" disabled>
                  {t('central.sites.details.markDelete')}
                </Button>
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={() => handleToggleMarkDelete(record.rowIndex)}
                >
                  {t('central.sites.details.cancelDelete')}
                </Button>
              </div>
            );
          }
          // 默认态：仅展示删除图标，点击后进入拟删除态（本地标记，确认生效时才真正删除）
          return (
            <div className="flex justify-start">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleToggleMarkDelete(record.rowIndex)}
              />
            </div>
          );
        }
        return (
          <div className="flex justify-start">
            <Button
                type="text"
                danger
                size="small"
                onClick={() => handleDeleteArea(record.rowIndex)}
            >
              <DeleteOutlined/>
            </Button>
          </div>
        );
      },
    }] : []),
  ];

  // 拟删除行「整行置灰」：把 opacity 施加到数据单元格而非整行，
  // 使操作列（拟删除/取消删除按钮）保持全色，取消删除红字不被淡化
  const isMarkedRow = (record: InternalAreaType) =>
    isGrayWarehouse && !!record.id && markedForDeletion.has(record.id);
  const grayColumns = columns.map(col =>
    col.key === 'actions'
      ? col
      : {
          ...col,
          onCell: (record: InternalAreaType) =>
            (isMarkedRow(record) ? { className: 'opacity-50' } : {}),
        },
  );

  // 内部保存处理函数
  const handleSave = () => {
    // 检查是否有数据需要保存
    if (internalAreas.length === 0) {
      // 没有数据时直接切换到只读状态，不执行保存接口
      onCancel?.();
      return;
    }

    if (isGrayWarehouse) {
      handleConfirmGray();
      return;
    }

    // 保存时验证所有行（不跳过空行）
    const saveErrors = validateAreas(internalAreas, false);
    if (saveErrors.length > 0) {
      setValidationErrors(saveErrors);
      message.error('Please fix the validation errors before saving');
      return;
    }
    const formData: AreaZipcodesFormData = {
      areas: internalAreas.map(area => ({
        id: area.id,
        name: area.name,
        warehouseId: area.warehouseId,
        dspId: area.dspId,
        zipcodeList: area.zipcodeList,
        sortingCode: area.sortingCode
      }))
    };
    onSave?.(formData);
  };

  // 灰度仓：二次确认后确认生效
  const handleConfirmGray = async () => {
    // 拟删除行不参与保存与校验
    const activeAreas = internalAreas.filter(a => !(a.id && markedForDeletion.has(a.id)));
    const saveErrors = validateAreas(activeAreas, false);
    if (saveErrors.length > 0) {
      setValidationErrors(saveErrors);
      message.error('Please fix the validation errors before saving');
      return;
    }
    const routeCount = activeAreas.length;
    const zipcodeCount = new Set(activeAreas.flatMap(a => a.zipcodeList)).size;
    const areasPayload: AreaTypeInput[] = activeAreas.map(area => ({
      id: area.id || undefined,
      name: area.name,
      warehouseId: (area.warehouseId || warehouseId?.toString() || ''),
      dspId: area.dspId || undefined,
      zipcodeList: area.zipcodeList,
      sortingCode: area.sortingCode || undefined,
    }));
    const deletedAreaIds = Array.from(markedForDeletion)
      .map(id => parseInt(id, 10))
      .filter(id => !Number.isNaN(id));

    // 弹框前查询跨仓影响明细（按仓粒度）；查询失败不阻断确认流程，仅不展示跨仓行
    let crossItems: CrossWarehouseImpactItem[] = [];
    if (warehouseId) {
      try {
        const { data: impactData } = await getCrossWarehouseImpact({
          variables: { warehouseId, areas: areasPayload, deletedAreaIds },
          fetchPolicy: 'no-cache',
        });
        crossItems = impactData?.previewRouteZipcodeChangeCrossWarehouse?.items ?? [];
      } catch (error) {
        console.error('Failed to preview cross-warehouse impact:', error);
      }
    }

    const content = (
      <div>
        <div>{t('central.sites.details.confirmRouteChangeContent', { routeCount, zipcodeCount })}</div>
        {crossItems.length > 0 && (
          <div className="mt-2">
            <div>{t('central.sites.details.confirmRouteChangeCrossWarehouseTitle')}</div>
            {crossItems.map(item => (
              <div key={item.warehouseId} data-testid={`cross-warehouse-item-${item.warehouseId}`}>
                {t('central.sites.details.confirmRouteChangeCrossWarehouseItem', {
                  warehouseName: item.warehouseName || item.warehouseId,
                  routeCount: item.routeCount,
                  zipcodeCount: item.zipcodeCount,
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );

    Modal.confirm({
      title: t('central.sites.details.confirmRouteChangeTitle'),
      content,
      okText: t('central.sites.details.confirmRouteChangeOk'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        await onConfirmGray?.({ areas: areasPayload, deletedAreaIds });
      },
    });
  };

  // ==================== 导入 / 导出（灰度仓） ====================
  // 导出/导入列（除「数量/接单中」外全部页面字段 + 操作列），顺序固定：ID/Name/Zipcodes/SortingCode/AssignDsp/Action，导入按列位解析
  // 导出走后端 EasyExcel+POI（含操作列/车队列下拉、多语表头、邮编列加宽），与项目既有导出范式统一

  // 导入时把 en/zh/es 任一语言的操作标签回映射为后端中文标准值（后端 RouteImportAction 不动）
  const buildActionReverseMap = (): Map<string, string> => {
    const canonical: Array<[string, string]> = [
      ['routeActionAdd', '新增'],
      ['routeActionModify', '修改'],
      ['routeActionDelete', '删除'],
      ['routeActionUnchanged', '不变'],
    ];
    const map = new Map<string, string>();
    ['zh', 'en', 'es'].forEach(lng => {
      const tf = i18n.getFixedT(lng, undefined);
      canonical.forEach(([key, zh]) => {
        const label = String(tf(`central.sites.details.${key}`)).trim();
        if (label) map.set(label, zh);
      });
    });
    // 兜底：中文标准值本身恒等映射
    canonical.forEach(([, zh]) => map.set(zh, zh));
    return map;
  };

  const handleExport = async () => {
    if (!warehouseId) {
      message.error('warehouseId is missing');
      return;
    }
    try {
      // 文件名拼接下划线 + 时间戳，与项目既有导出命名（utils/exportUtils.ts）保持一致
      const baseName = t('central.sites.details.exportRoutesFileName', { warehouseName: warehouseName || warehouseId });
      const fileName = `${baseName}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      await downloadAreaRoutesExcel(warehouseId, fileName);
    } catch (error: any) {
      message.error(error?.message || t('central.sites.messages.exportFailed'));
    }
  };

  // 前端解析 xlsx → 调后端校验预览 → 覆盖编辑态；返回 false 阻止 antd 默认上传
  const handleImportFile: UploadProps['beforeUpload'] = async (file) => {
    if (!warehouseId) {
      message.error('warehouseId is missing');
      return Upload.LIST_IGNORE;
    }
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      // 跳过表头行
      const dataRows = matrix.slice(1).filter(r => r.some(cell => String(cell).trim() !== ''));
      // 内容全为空（仅表头或整表空白）：弹框提示并中止，避免以空 rows 调后端得到无变更却提示成功
      if (dataRows.length === 0) {
        showRouteChangeErrorModal(
          t('central.sites.messages.importEmpty'),
          t,
          t('central.sites.messages.importFailed') as string,
        );
        return Upload.LIST_IGNORE;
      }
      // 操作列标签回映射为后端中文标准值；查不到则原样传（后端兜底报错）
      const actionReverseMap = buildActionReverseMap();
      const rows: RouteImportRowInput[] = dataRows.map(r => {
        const rawAction = String(r[5] ?? '').trim();
        return {
          id: String(r[0] ?? '').trim() || undefined,
          name: String(r[1] ?? '').trim(),
          zipcodeList: String(r[2] ?? '').split(/[,，\s;]+/).map(z => z.trim()).filter(Boolean),
          sortingCode: String(r[3] ?? '').trim(),
          dspName: String(r[4] ?? '').trim(),
          action: actionReverseMap.get(rawAction) ?? rawAction,
        };
      });

      const { data: previewData } = await validateAndPreviewRouteImport({
        variables: { warehouseId, rows },
      });
      const preview = previewData?.validateAndPreviewRouteImport;
      if (!preview) {
        message.error(t('central.sites.messages.importFailed'));
        return Upload.LIST_IGNORE;
      }
      // 覆盖编辑态：按 Excel 行顺序回填，拟删除行在其原位置置灰显示（不追加到最后）
      const deletedIdSet = new Set(preview.deletedAreaIds.map(String));
      const toInternal = (a: AreaType): InternalAreaType => ({
        ...a,
        zipcodeListText: a.zipcodeList.join(', '),
      });
      // 保留/修改按 id 匹配（不受改名影响）；新增按 name 匹配（合并集内名唯一，后端已校验）
      const keptById = new Map(preview.areas.filter(a => a.id).map(a => [String(a.id), a]));
      const newByName = new Map(preview.areas.filter(a => !a.id).map(a => [a.name, a]));
      const deletedById = new Map(
        data.areas.filter(a => deletedIdSet.has(a.id)).map(a => [a.id, a]),
      );
      const usedIds = new Set<string>();
      const usedNewNames = new Set<string>();
      const ordered: InternalAreaType[] = [];
      rows.forEach(row => {
        const rid = row.id?.trim();
        if (rid && deletedIdSet.has(rid)) {
          const del = deletedById.get(rid);
          if (del) ordered.push(toInternal(del));
        } else if (rid && keptById.has(rid)) {
          ordered.push(toInternal(keptById.get(rid)!));
          usedIds.add(rid);
        } else if (!rid) {
          const na = newByName.get(row.name?.trim() ?? '');
          if (na) {
            ordered.push(toInternal(na));
            usedNewNames.add(na.name);
          }
        }
      });
      // 兜底：增量导入下未出现在文件中的既有保留路线（无对应行），追加到末尾避免丢失
      preview.areas.forEach(a => {
        if (a.id && !usedIds.has(String(a.id))) ordered.push(toInternal(a));
        else if (!a.id && !usedNewNames.has(a.name)) ordered.push(toInternal(a));
      });
      setInternalAreas(ordered);
      setMarkedForDeletion(deletedIdSet);
      setValidationErrors([]);
      setCurrentPage(1);
      message.success(t('central.sites.messages.importPreviewLoaded'));
    } catch (error: any) {
      const graphQLError = error?.graphQLErrors?.[0];
      const backendMessage: string | undefined = graphQLError?.extensions?.message || graphQLError?.message;
      // 导入校验类报错（如「邮编 X 属于多条路线」）改为常驻弹框，便于查看/复制邮编
      showRouteChangeErrorModal(
        backendMessage || error?.message || t('central.sites.messages.importFailed'),
        t,
        t('central.sites.messages.importFailed') as string,
      );
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <EditableCard
      title={title || t('central.sites.details.areaZipcodes')}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
      updating={isGrayWarehouse && updateInProgress}
      updatingText={t('central.sites.details.updatingParcelData')}
      editHint={isGrayWarehouse ? t('central.sites.details.confirmAfterEditHint') : undefined}
    >
      {/* 显示验证错误 */}
      {validationErrors.length > 0 && (
        <Alert
          type="error"
          message="Validation Errors"
          description={
            <ul className="mb-0">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          className="mb-4"
          showIcon
        />
      )}
      
      {/* 显示可用 zipcodes 提示 */}
      {isEditing && deliveryZoneZipcodes.length > 0 && (
        <Alert
          type="info"
          message="Available Zipcodes"
          description={`You can only use zipcodes from the delivery zone: ${deliveryZoneZipcodes.join(', ')}`}
          className="mb-4"
          showIcon
        />
      )}

      {/* 灰度仓：导入 / 导出工具栏 */}
      {isGrayWarehouse && isEditing && (
        <div className="flex gap-2 mb-3">
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            {t('central.sites.details.exportRoutes')}
          </Button>
          <Upload
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={handleImportFile}
          >
            <Button icon={<UploadOutlined />}>{t('central.sites.details.importRoutes')}</Button>
          </Upload>
        </div>
      )}

      <Table
            dataSource={(() => {
              const startIndex = (currentPage - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              const pageData = areasWithIndex.slice(startIndex, endIndex);

              // 如果当前页没有数据，自动跳转到最后一页
              if (pageData.length === 0 && areasWithIndex.length > 0) {
                const lastPage = Math.ceil(areasWithIndex.length / pageSize);
                if (lastPage !== currentPage) {
                  setCurrentPage(lastPage);
                }
                // 返回最后一页的数据
                const lastPageStartIndex = (lastPage - 1) * pageSize;
                return areasWithIndex.slice(lastPageStartIndex, lastPageStartIndex + pageSize);
              }

              return pageData;
            })()}
            columns={grayColumns}
            bordered={false}
            rowKey="rowIndex"
            size="small"
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 'max-content' }}
            className="mb-4"
        />
      {isEditing && (
        <div className="mt-4 border border-dashed rounded-md p-2 text-center">
          <Button
            type="text"
            icon={<PlusOutlined />}
            className="text-purple-500"
            onClick={handleAddSection}
          />
        </div>
      )}
      <div>
        <TablePaginationBar
          total={areasWithIndex.length}
          current={currentPage}
          pageSize={pageSize}
          onChange={(page) => {
            setCurrentPage(page);
          }}
          onShowSizeChange={(_, size) => {
            setCurrentPage(1); // 先重置到第一页
            setPageSize(size); // 再设置新的页面大小
          }}
          pageSizeOptions={['5', '10', '20', '50', '100']}
          paginationProps={{ responsive: true }}
        />
        <div className="text-gray-500 mt-1">
          {t('central.sites.details.totalZipcodes')}: {internalAreas.reduce((total, area) => total + area.zipcodeList.length, 0)}
        </div>
      </div>
    </EditableCard>
  );
};

export default AreaZipcodesForm;
